import { Injectable } from '@angular/core'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { CompanyManagementService } from '@app/features/company-management/service/company-management.service'
import { forkJoin, Observable, of } from 'rxjs'
import { map, catchError } from 'rxjs/operators'

export interface RoleMappingResult {
  mapping: Record<number, any> | null
  isNeededToMap: boolean
}

@Injectable({
  providedIn: 'root',
})
export class RoleMappingService {
  private static readonly DEFAUL_DEPTH = 1

  constructor(private companyManagementService: CompanyManagementService) {}

  buildRolesMapping(
    deliverables: Deliverable[],
    targetRateCard: RatecardVersion,
    sourceRateCardVersion?: number
  ): Observable<RoleMappingResult> {
    if (!targetRateCard.departments) {
      return of({ mapping: null, isNeededToMap: true })
    }

    const targetRolesByNameAndDept = new Map<string, any>()
    const targetRolesById = new Map<number, any>()
    const departmentsByRoleId = new Map<number, string>()

    for (const dept of targetRateCard.departments) {
      for (const targetRole of dept.roles) {
        const roleKey = this.getRoleKey(targetRole.name, dept.name)
        targetRolesByNameAndDept.set(roleKey, targetRole)
        departmentsByRoleId.set(targetRole.id, dept.name)

        const previousRoleId = (targetRole as any).previousRoleVersion?.id
        if (previousRoleId) {
          targetRolesById.set(previousRoleId, targetRole)
        }
        const scopeMarkMappings = (targetRole as any).ratecardRoleScopeMarkRoleMappings
        if (Array.isArray(scopeMarkMappings)) {
          scopeMarkMappings.forEach((m) => {
            if (m.scopeMarkRoleId) {
              targetRolesById.set(m.scopeMarkRoleId, targetRole)
            }
          })
        }
      }
    }

    const sourceRoles = deliverables
      .flatMap((d) => d.getAllComponents())
      .flatMap((c) => c.departments.flatMap((dept) => dept.roles))
      .filter((r) => r.sourceType !== 'SCOPE')

    const mapping: Record<number, any> = {}
    const unmappedSourceRoles: any[] = []

    for (const sourceRole of sourceRoles) {
      const roleKey = this.getRoleKey(sourceRole.source.name, sourceRole.departmentType.name)
      const targetRole = targetRolesByNameAndDept.get(roleKey) || targetRolesById.get(sourceRole.source.id)

      if (targetRole) {
        mapping[sourceRole.source.id] = targetRole
      } else {
        unmappedSourceRoles.push(sourceRole)
      }
    }

    if (unmappedSourceRoles.length === 0) {
      return of({ mapping, isNeededToMap: false })
    }

    const depth = this.getSearchDepth(targetRateCard.version, sourceRateCardVersion)
    const allTargetRoles = targetRateCard.departments.flatMap((dept) => dept.roles)

    const chainRequests = allTargetRoles.map((targetRole) =>
      this.companyManagementService.getAllPreviousRolesChain(targetRole.id, depth).pipe(
        map((chain) => ({ targetRole, chain })),
        catchError(() => of({ targetRole, chain: [] }))
      )
    )

    return forkJoin(chainRequests).pipe(
      map((results) => this.findInPreviousChain(results, unmappedSourceRoles, departmentsByRoleId, mapping))
    )
  }

  private findInPreviousChain(
    results: { targetRole: any; chain: any[] }[],
    unmappedSourceRoles: any[],
    departmentsByRoleId: Map<number, string>,
    mapping: Record<number, any>
  ): RoleMappingResult {
    const unmappedById = new Map<number, any>()
    const unmappedByKey = new Map<string, any>()

    unmappedSourceRoles.forEach((sourceRole) => {
      const roleKey = this.getRoleKey(sourceRole.source.name, sourceRole.departmentType.name)
      unmappedById.set(sourceRole.source.id, sourceRole)
      unmappedByKey.set(roleKey, sourceRole)
    })

    results.forEach(({ targetRole, chain }) => {
      const targetDepartment = departmentsByRoleId.get(targetRole.id)!

      chain.forEach((previousRole) => {
        let sourceRole = unmappedById.get(previousRole.id)

        if (!sourceRole && previousRole.name) {
          const roleKey = this.getRoleKey(previousRole.name, targetDepartment)
          sourceRole = unmappedByKey.get(roleKey)
        }

        if (sourceRole && unmappedById.has(sourceRole.source.id)) {
          mapping[sourceRole.source.id] = targetRole
          unmappedById.delete(sourceRole.source.id)
          const roleKey = this.getRoleKey(sourceRole.source.name, sourceRole.departmentType.name)
          unmappedByKey.delete(roleKey)
        }
      })
    })

    if (unmappedById.size > 0) {
      return { mapping: null, isNeededToMap: true }
    }

    return { mapping, isNeededToMap: false }
  }

  private getSearchDepth(targetVersion: number, sourceVersion?: number): number {
    if (sourceVersion && targetVersion > sourceVersion) {
      return targetVersion - sourceVersion
    }
    return RoleMappingService.DEFAUL_DEPTH
  }

  private getRoleKey(name: string, dept: string): string {
    return `${name.toLowerCase()} : ${dept}`
  }
}
